<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'

import AppLayout from '@/components/layout/AppLayout.vue'
import BadgeUnlockDialog from '@/components/gamification/BadgeUnlockDialog.vue'
import { useBadgeNotification, setBadgeNotificationCallback } from '@/composables/useBadgeNotification'

const route = useRoute()
const authStore = useAuthStore()
const uiStore = useUiStore()

const isLoading = computed(() => authStore.isLoading)
const currentMode = computed(() => uiStore.mode)
const isLoginPage = computed(() => route.name === 'login')

// Badge notification system
const {
  showDialog: showBadgeDialog,
  currentBadge,
  showBadgeUnlock,
  closeBadgeNotification,
} = useBadgeNotification()

// Register global callback for badge notifications
onMounted(() => {
  setBadgeNotificationCallback(showBadgeUnlock)
})
</script>

<template>
  <div
    :class="[
      'min-h-screen bg-gray-50 dark:bg-gray-900',
      { 'dark': uiStore.isDark },
      `mode-${currentMode}`
    ]"
    :dir="uiStore.isRtl ? 'rtl' : 'ltr'"
  >
    <Toast position="top-right" />
    <ConfirmDialog />
    <BadgeUnlockDialog
      v-model:visible="showBadgeDialog"
      :badge="currentBadge"
      @close="closeBadgeNotification"
    />

    <!-- Loading state (during Keycloak init) -->
    <div v-if="isLoading" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
        <p class="mt-4 text-gray-600 dark:text-gray-400">{{ $t('common.loading') }}</p>
      </div>
    </div>

    <!-- Login page without layout -->
    <RouterView v-else-if="isLoginPage" />

    <!-- Authenticated layout -->
    <AppLayout v-else>
      <RouterView />
    </AppLayout>
  </div>
</template>

<style>
/* Mode-specific styles */
.mode-wordless {
  /* Hide all text for wordless mode */
}

.mode-wordless .hide-in-wordless {
  display: none !important;
}
</style>
